import { observer } from 'mobx-react-lite';
import { Plus, Search, Pencil, Trash2, Loader2, AlertTriangle, Eye, EyeOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { PageHeader } from '@/components/layout/PageHeader';
import { useTariffController } from '@/controllers/tariff/useTariffController';
import { cn } from '@/lib/utils';
import { currencySign } from '@/lib/currency';
import { StartTariffBadge } from './StartTariffBadge';

// value — значения is_start из справочника биллинга
const START_TARIFF_OPTIONS = [
  { value: '0', label: 'Нет' },
  { value: '1', label: 'Только в первый раз' },
  { value: '2', label: 'Основной' },
  { value: '3', label: 'После триала' },
];

const LVM_START_OPTIONS = [
  { value: '', label: 'Нет' },
  { value: '2', label: 'Стартовый' },
  { value: '3', label: 'После триала' },
];

function formatAbPlata(amount: number, currencyLabel: string): string {
  return `${amount.toLocaleString('ru-RU')} ${currencyLabel}`.trimEnd();
}

export const TariffsPage = observer(function TariffsPage() {
  const tariffStore = useTariffController();
  const lvmStartActive =
    tariffStore.formData.start_tariff_lv_monitor === '2' ||
    tariffStore.formData.start_tariff_lv_monitor === '3';
  const showAppColumn = tariffStore.hasAppVisibility;
  const formCurrency = currencySign(
    tariffStore.countries.find((country) => country.id_country === tariffStore.formData.id_country)
  );

  return (
    <div className='min-h-screen bg-background'>
      <PageHeader activeTab='tariffs' />

      <div className='px-6 pb-12'>
        <div className='flex items-center justify-between mb-6 gap-4 flex-wrap'>
          <h1 className='text-2xl font-medium text-foreground'>Управление тарифами</h1>
          <Button onClick={() => tariffStore.openCreate()} className='gap-2'>
            <Plus className='w-4 h-4' />
            Создать тариф
          </Button>
        </div>

        {/* Search */}
        <div className='relative mb-4'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none' />
          <Input
            placeholder='Поиск по названию или описанию'
            value={tariffStore.search}
            onChange={(event) => tariffStore.setSearch(event.target.value)}
            className='pl-9'
          />
        </div>

        {/* Table */}
        <div className='relative'>
          {tariffStore.isRefreshing && (
            <div className='absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-background/60 backdrop-blur-[2px]'>
              <Loader2 className='w-5 h-5 animate-spin text-muted-foreground' />
            </div>
          )}
          <div className='rounded-xl border border-border/50 overflow-x-auto bg-card'>
            {tariffStore.isLoading ? (
              <div className='flex items-center justify-center gap-2 py-16 text-muted-foreground'>
                <Loader2 className='w-5 h-5 animate-spin' />
                Загрузка тарифов...
              </div>
            ) : tariffStore.error ? (
              <div className='flex items-center justify-center gap-2 py-16 text-destructive'>
                <AlertTriangle className='w-5 h-5' />
                {tariffStore.error}
              </div>
            ) : (
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-border/50 bg-muted/30'>
                    <th className='text-left px-4 py-3 font-medium text-muted-foreground'>
                      Название
                    </th>
                    <th className='text-left px-4 py-3 font-medium text-muted-foreground'>
                      Абонплата
                    </th>
                    <th className='text-left px-4 py-3 font-medium text-muted-foreground'>
                      Пробный
                    </th>
                    {showAppColumn && (
                      <th className='text-left px-4 py-3 font-medium text-muted-foreground whitespace-nowrap'>
                        В приложении
                      </th>
                    )}
                    <th className='text-left px-4 py-3 font-medium text-muted-foreground'>Камер</th>
                    <th className='text-left px-4 py-3 font-medium text-muted-foreground'>
                      Страна
                    </th>
                    <th className='text-left px-4 py-3 font-medium text-muted-foreground'>
                      Тип клиента
                    </th>
                    <th className='px-4 py-3' />
                  </tr>
                </thead>
                <tbody>
                  {tariffStore.filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showAppColumn ? 8 : 7}
                        className='text-center py-16 text-muted-foreground'
                      >
                        {tariffStore.search ? 'Тарифы не найдены' : 'Тарифов пока нет'}
                      </td>
                    </tr>
                  ) : (
                    tariffStore.filtered.map((tariff) => (
                      <tr
                        key={tariff.id_tariff}
                        className='border-b border-border/30 last:border-0 hover:bg-muted/20 transition-colors'
                      >
                        <td className='px-4 py-3'>
                          <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                            <span className='font-medium text-foreground'>
                              {tariff.tariff_name ?? '—'}
                            </span>
                            <StartTariffBadge isStart={tariff.is_start} />
                            {tariff.start_tariff_lv_monitor === 2 && (
                              <span className='text-xs px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 font-medium'>
                                LVM: Стартовый
                              </span>
                            )}
                            {tariff.start_tariff_lv_monitor === 3 && (
                              <span className='text-xs px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400 font-medium'>
                                LVM: После триала
                              </span>
                            )}
                          </div>
                          {tariff.description && (
                            <p className='text-xs text-muted-foreground mt-0.5 line-clamp-1'>
                              {tariff.description}
                            </p>
                          )}
                        </td>
                        <td className='px-4 py-3 font-medium text-foreground'>
                          {tariff.full_ab_plata != null
                            ? formatAbPlata(
                                tariff.full_ab_plata,
                                tariffStore.countryCurrencyMap.get(tariff.country) ?? ''
                              )
                            : '—'}
                        </td>
                        <td className='px-4 py-3 text-muted-foreground'>{tariff.test_days} дн.</td>
                        {showAppColumn && (
                          <td className='px-4 py-3'>
                            {tariff.is_showed === 1 ? (
                              <span title='Показывается в приложении'>
                                <Eye className='w-5 h-5 text-primary' />
                              </span>
                            ) : (
                              <span title='Не показывается в приложении'>
                                <EyeOff className='w-5 h-5 text-muted-foreground/50' />
                              </span>
                            )}
                          </td>
                        )}
                        <td className='px-4 py-3 text-muted-foreground'>
                          {tariff.count_cameras ?? '—'}
                        </td>
                        <td className='px-4 py-3 text-muted-foreground'>{tariff.country || '—'}</td>
                        <td className='px-4 py-3'>
                          {tariff.account_type_name && (
                            <span className='text-xs px-1.5 py-0.5 rounded-full bg-muted/60 text-muted-foreground w-fit font-medium whitespace-nowrap'>
                              {tariff.account_type_name}
                            </span>
                          )}
                        </td>
                        <td className='px-4 py-3'>
                          <div className='flex items-center gap-1'>
                            <Button
                              variant='ghost'
                              size='icon'
                              onClick={() => tariffStore.openEdit(tariff)}
                              title='Редактировать'
                              className='p-1.5 h-auto w-auto rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary'
                            >
                              <Pencil className='w-4 h-4' />
                            </Button>
                            <Button
                              variant='ghost'
                              size='icon'
                              onClick={() => tariffStore.setDeleteTarget(tariff)}
                              title='Удалить'
                              className='p-1.5 h-auto w-auto rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive'
                            >
                              <Trash2 className='w-4 h-4' />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {tariffStore.isModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div className='absolute inset-0 bg-black/50' onClick={() => tariffStore.closeModal()} />
          <div className='relative w-full max-w-2xl rounded-2xl border border-border/50 bg-card shadow-2xl'>
            <div className='flex items-center justify-between px-6 py-4 border-b border-border/50'>
              <h2 className='text-base font-semibold tracking-wide text-foreground'>
                {tariffStore.editingTariff ? 'Редактирование тарифа' : 'Новый тариф'}
              </h2>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => tariffStore.closeModal()}
                className='p-1 h-auto w-auto rounded-lg hover:bg-muted/50 text-muted-foreground'
              >
                <X className='w-4 h-4' />
              </Button>
            </div>

            <div className='px-6 py-5 flex flex-col gap-4 overflow-y-auto max-h-[70vh]'>
              {tariffStore.formError && (
                <div className='flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm'>
                  <AlertTriangle className='w-4 h-4 shrink-0' />
                  {tariffStore.formError}
                </div>
              )}

              <div className='flex flex-col gap-1.5'>
                <Label className='text-sm font-medium text-foreground'>
                  Название <span className='text-destructive'>*</span>
                </Label>
                <Input
                  value={tariffStore.formData.tariff_name}
                  onChange={(event) => tariffStore.setField('tariff_name', event.target.value)}
                  placeholder='Базовый'
                />
              </div>

              <div className='flex flex-col gap-1.5'>
                <Label className='text-sm font-medium text-foreground'>Описание</Label>
                <textarea
                  value={tariffStore.formData.description}
                  onChange={(event) => tariffStore.setField('description', event.target.value)}
                  placeholder='Краткое описание тарифа'
                  rows={2}
                  className='flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none'
                />
              </div>

              <div className='flex flex-col gap-1.5'>
                <Label className='text-sm font-medium text-foreground'>Описание для аккаунта</Label>
                <textarea
                  value={tariffStore.formData.account_description}
                  onChange={(event) =>
                    tariffStore.setField('account_description', event.target.value)
                  }
                  placeholder='Описание для клиента'
                  rows={2}
                  className='flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none'
                />
              </div>

              <div className='flex flex-col gap-1.5'>
                <Label className='text-sm font-medium text-foreground'>
                  Описание рекомендованной оплаты
                </Label>
                <Input
                  value={tariffStore.formData.desc_recomm_pay}
                  onChange={(event) => tariffStore.setField('desc_recomm_pay', event.target.value)}
                  placeholder='Описание рекомендованной оплаты'
                />
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    Страна <span className='text-destructive'>*</span>
                  </Label>
                  <select
                    value={tariffStore.formData.id_country}
                    onChange={(event) => tariffStore.setField('id_country', event.target.value)}
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  >
                    <option value=''>— выберите —</option>
                    {tariffStore.countries.map((country) => (
                      <option key={country.id_country} value={country.id_country}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    Тип аккаунта <span className='text-destructive'>*</span>
                  </Label>
                  <select
                    value={tariffStore.formData.id_account_type}
                    onChange={(event) =>
                      tariffStore.setField('id_account_type', event.target.value)
                    }
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  >
                    <option value=''>— выберите —</option>
                    {tariffStore.accountTypes.map((accountType) => (
                      <option key={accountType.id_account_type} value={accountType.id_account_type}>
                        {accountType.account_type_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    {formCurrency ? `Абонплата (${formCurrency})` : 'Абонплата'}
                  </Label>
                  <Input
                    type='number'
                    min='0'
                    value={tariffStore.formData.ab_plata}
                    onChange={(event) => tariffStore.setField('ab_plata', event.target.value)}
                    placeholder='500'
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>Глубина архива</Label>
                  <select
                    value={tariffStore.formData.id_archive_period}
                    onChange={(event) =>
                      tariffStore.setField('id_archive_period', event.target.value)
                    }
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  >
                    <option value=''>— выберите —</option>
                    {tariffStore.archiveDepths.map((depth) => (
                      <option key={depth.id_archive_period} value={depth.id_archive_period}>
                        {depth.archive_period_name} ({depth.days} дн.)
                      </option>
                    ))}
                  </select>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    Пробный период (дн.)
                  </Label>
                  <Input
                    type='number'
                    min='0'
                    value={tariffStore.formData.test_days}
                    onChange={(event) => tariffStore.setField('test_days', event.target.value)}
                    placeholder='0'
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>Длина тарифа (дн.)</Label>
                  <Input
                    type='number'
                    min='1'
                    value={tariffStore.formData.tariff_length}
                    onChange={(event) => {
                      const v = event.target.value;
                      tariffStore.setField('tariff_length', v === '' || parseInt(v) > 0 ? v : '');
                    }}
                    placeholder='30'
                  />
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div className='flex flex-col gap-1.5'>
                    <Label className='text-sm font-medium text-foreground'>Кол-во периодов</Label>
                    <Input
                      type='number'
                      min='0'
                      value={tariffStore.formData.periods_count}
                      onChange={(event) =>
                        tariffStore.setField('periods_count', event.target.value)
                      }
                      placeholder='0'
                    />
                  </div>
                  <div className='flex flex-col gap-1.5'>
                    <Label className='text-sm font-medium text-foreground'>ID тарифа VC</Label>
                    <Input
                      type='number'
                      min='0'
                      value={tariffStore.formData.id_tariff_vc}
                      onChange={(event) => tariffStore.setField('id_tariff_vc', event.target.value)}
                      placeholder='0'
                    />
                  </div>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    Мин. кол-во камер для показа
                  </Label>
                  <Input
                    type='number'
                    min='0'
                    value={tariffStore.formData.show_if_count_abon_cameras_is}
                    onChange={(event) =>
                      tariffStore.setField('show_if_count_abon_cameras_is', event.target.value)
                    }
                    placeholder='0'
                  />
                </div>
                <div className='col-span-2 flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>Стартовый тариф</Label>
                  <SegmentedControl
                    aria-label='Стартовый тариф'
                    options={START_TARIFF_OPTIONS}
                    value={tariffStore.formData.is_start}
                    disabled={lvmStartActive}
                    onChange={(value) => tariffStore.setField('is_start', value)}
                  />
                </div>
                <Label
                  className={cn(
                    'flex items-center gap-2.5',
                    lvmStartActive ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                  )}
                >
                  <Checkbox
                    checked={Boolean(tariffStore.formData.is_tariff_lv_monitor)}
                    disabled={lvmStartActive}
                    onCheckedChange={(checked) => {
                      tariffStore.setField('is_tariff_lv_monitor', Boolean(checked));
                      if (!checked) tariffStore.setField('start_tariff_lv_monitor', '');
                    }}
                  />
                  <span className='text-sm text-foreground'>Тариф LV Monitor</span>
                </Label>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    Нач. тариф LV Monitor
                  </Label>
                  <SegmentedControl
                    aria-label='Нач. тариф LV Monitor'
                    options={LVM_START_OPTIONS}
                    value={tariffStore.formData.start_tariff_lv_monitor}
                    onChange={(value) => {
                      tariffStore.setField('start_tariff_lv_monitor', value);
                      if (value === '2' || value === '3') {
                        tariffStore.setField('is_start', '0');
                        tariffStore.setField('is_tariff_lv_monitor', true);
                      } else {
                        tariffStore.setField('is_tariff_lv_monitor', false);
                      }
                    }}
                  />
                </div>
              </div>

              <div className='flex flex-col gap-2 pt-1 border-t border-border/30'>
                {(
                  [
                    ['is_showed', 'Показывать в приложении'],
                    ['for_oper', 'Показывать менеджеру в CRM'],
                    ['users_choice', 'Выбор пользователя'],
                    ['has_discount', 'Есть скидка'],
                    ['for_move', 'Для переноса'],
                    ['no_show_tariff_with_location', 'Скрыть тарифы с локацией'],
                  ] as [keyof typeof tariffStore.formData, string][]
                ).map(([field, label]) => (
                  <Label key={field} className='flex items-center gap-2.5 cursor-pointer'>
                    <Checkbox
                      checked={Boolean(tariffStore.formData[field])}
                      onCheckedChange={(checked) => tariffStore.setField(field, Boolean(checked))}
                    />
                    <span className='text-sm text-foreground'>{label}</span>
                  </Label>
                ))}
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    Тариф после бонусного периода
                  </Label>
                  <select
                    value={tariffStore.formData.id_tariff_after_bonus}
                    onChange={(event) =>
                      tariffStore.setField('id_tariff_after_bonus', event.target.value)
                    }
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  >
                    <option value=''>— не выбран —</option>
                    {tariffStore.tariffs.map((tariff) => (
                      <option key={tariff.id_tariff} value={tariff.id_tariff}>
                        {tariff.tariff_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <Label className='text-sm font-medium text-foreground'>
                    Дни бонусного периода
                  </Label>
                  <Input
                    type='number'
                    min='0'
                    value={tariffStore.formData.bonus_days}
                    onChange={(event) => tariffStore.setField('bonus_days', event.target.value)}
                    placeholder='0'
                  />
                </div>
              </div>
            </div>

            <div className='flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50'>
              <Button
                variant='outline'
                onClick={() => tariffStore.closeModal()}
                disabled={tariffStore.isSaving}
              >
                Отмена
              </Button>
              <Button
                onClick={() => tariffStore.save()}
                disabled={tariffStore.isSaving}
                className='gap-2'
              >
                {tariffStore.isSaving && <Loader2 className='w-4 h-4 animate-spin' />}
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {tariffStore.deleteTarget && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div
            className='absolute inset-0 bg-black/50'
            onClick={() => tariffStore.setDeleteTarget(null)}
          />
          <div className='relative w-full max-w-sm rounded-2xl border border-border/50 bg-card shadow-2xl'>
            <div className='px-6 py-5'>
              <h2 className='text-base font-semibold text-foreground mb-2'>Удалить тариф?</h2>
              <p className='text-sm text-muted-foreground'>
                Тариф{' '}
                <span className='font-medium text-foreground'>
                  «{tariffStore.deleteTarget.tariff_name}»
                </span>{' '}
                будет удалён безвозвратно.
              </p>
            </div>
            <div className='flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50'>
              <Button
                variant='outline'
                onClick={() => tariffStore.setDeleteTarget(null)}
                disabled={tariffStore.isDeleting}
              >
                Отмена
              </Button>
              <Button
                variant='destructive'
                onClick={() => tariffStore.confirmDelete()}
                disabled={tariffStore.isDeleting}
                className='gap-2'
              >
                {tariffStore.isDeleting && <Loader2 className='w-4 h-4 animate-spin' />}
                Удалить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
